import { useState } from 'react'
import type { AppConfig, Region } from '../types'
import type { Action } from '../booking/state'
import { track } from '../analytics'
import { isCompleteZip } from '../validation'
import { requestGeolocation } from '../geo'
import { Icon } from '../icons'

/**
 * 7.4 / §1 Choose a location. Three ways in, all region-agnostic to the API
 * except a named region:
 *   - type a 5-digit ZIP and search (§2.3 — only sent when exactly 5 digits),
 *   - press the target icon to locate by device GPS (§1.4, always re-queried §1.5),
 *   - pick a named region (which may nest one level into sub-regions).
 */
export function RegionStep({
  config,
  dispatch,
  onBack,
  embedded
}: {
  config: AppConfig
  dispatch: React.Dispatch<Action>
  onBack: () => void
  embedded?: boolean
}) {
  const [parent, setParent] = useState<Region | null>(null)
  const [zip, setZip] = useState('')
  const [locating, setLocating] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const heading = config.copy?.regionHeading || 'Where would you like to be seen?'

  const choose = (r: Region) => {
    if (r.sub_regions && r.sub_regions.length) {
      setParent(r)
      return
    }
    track('select_region', { region: r.id, name: r.name })
    dispatch({ type: 'SELECT_REGION', region: r })
  }

  const submitZip = () => {
    if (!isCompleteZip(zip)) {
      setErr('Please enter a 5-digit ZIP code.')
      return
    }
    setErr(null)
    track('submit_zip', { zip })
    dispatch({ type: 'SUBMIT_ZIP', zip })
  }

  // §1.5: always re-query a fresh fix so pressing locate works every time.
  const locate = async () => {
    setErr(null)
    setLocating(true)
    const coords = await requestGeolocation(8000, true)
    setLocating(false)
    if (coords) {
      track('use_location')
      dispatch({ type: 'USE_LOCATION', coords })
    } else {
      setErr('We couldn’t access your location. Enter a ZIP code instead.')
    }
  }

  const list = parent ? parent.sub_regions ?? [] : config.regions
  const back = () => (parent ? setParent(null) : onBack())

  return (
    <section>
      <h1 className="step-heading">{parent ? parent.name : heading}</h1>
      <p className="step-sub">{parent ? 'Choose the location nearest you.' : 'Search by ZIP, use your location, or pick an area.'}</p>

      {!parent && (
        <>
          <div className="zip-bar">
            <input
              className="zip-input"
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              placeholder="Enter ZIP code"
              maxLength={5}
              value={zip}
              aria-label="ZIP code"
              onChange={(e) => {
                setZip(e.target.value.replace(/\D/g, '').slice(0, 5))
                setErr(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  submitZip()
                }
              }}
            />
            <button
              type="button"
              className="btn btn-primary zip-go"
              onClick={submitZip}
              disabled={zip.length !== 5}
            >
              Search
            </button>
            <button
              type="button"
              className="zip-locate"
              onClick={locate}
              disabled={locating}
              aria-label="Use my current location"
              title="Use my current location"
            >
              <Icon value="target" size={22} />
            </button>
          </div>
          {locating && <p className="zip-hint">Finding your location…</p>}
          {err && (
            <p className="err" role="alert">
              {err}
            </p>
          )}

          <div className="or-divider" aria-hidden>
            <span>or</span>
          </div>
        </>
      )}

      {/* `regions` = compact tiles: the ZIP/locate bar above is the primary
          path, so the area list stays secondary rather than dominating. */}
      <div className="card-grid regions">
        {list.map((r, i) => (
          <button key={`${r.id}-${i}`} className="choice-card" onClick={() => choose(r)}>
            <span className="title">{r.name}</span>
            {r.sub_regions && r.sub_regions.length ? (
              <span className="desc">{r.sub_regions.length} locations →</span>
            ) : r.virtual === 1 ? (
              <span className="desc">Virtual / telehealth</span>
            ) : null}
          </button>
        ))}
      </div>

      {(!embedded || parent) && (
        <div className="btn-row">
          <button className="btn btn-ghost" onClick={back}>
            ← Back
          </button>
        </div>
      )}
    </section>
  )
}
